import React, { useCallback, useMemo, useState } from 'react'
import {
    StyledAuthorsView,
    StyledImageView,
    StyledLessButton,
    StyledLessButtonText,
    StyledMainView,
    StyledOpenedMainView,
    TitleText,
    StyledNameText,
    StyledShadowView,
    StyledCountText,
    StyledSquareIcon
} from './ReportersLiveStyled'
import { AuthorImage } from './AuthorImage'
import { ReportingLiveText } from './ReportingLiveText'
import { EventPostV4DTO } from '@west-australian-newspapers/publication-types'
import { useTheme } from '@emotion/react'
import { IconChevronDownTN } from '../../../icons/IconChevronDownTN/IconChevronDownTN'

export type Reporter = {
    name: string
    slug: string
    mainPhoto: string | undefined
    thumbnailPhoto: string | undefined
}

type ReportersProps = {
    authors?: Reporter[]
}

type ShowReportersLiveProps = {
    isActiveEvent: boolean
    publicationKind: string
    activePosts: EventPostV4DTO[]
}

export const ShowReportersLive = ({
    publicationKind,
    activePosts,
    isActiveEvent
}:ShowReportersLiveProps) => {
    const theme = useTheme()
    const isTheNightly = theme.kind === 'thenightly'

    const reportersLive = useMemo(
        () =>
            isActiveEvent
                ? activePosts
                    .map(
                        (post) => {
                            // reporter can either be a author with name, slug and thumbnail image
                            // or a byline text with reporter name
                            const profiles = post.profiles
                            if (Array.isArray(profiles) && profiles.length > 0) {
                                return profiles
                            }
                            return ({ name: post.byline } as Reporter)
                        }
                    )
                    //remove duplicates
                    .reduce((newArray, current) => {
                        if (Array.isArray(current)) {
                            current.forEach((reporter) => {
                                if (
                                    !newArray.some((r) => r.name === reporter.name)
                                ) {
                                    newArray.push(reporter)
                                }
                            })
                        } else {
                            if (
                                !newArray.some((r) => r.name === current.name)
                            ) {
                                newArray.push(current)
                            }
                        }
                        
                        return newArray
                    }, [] as Reporter[])
                : [],
        [activePosts, isActiveEvent],
    )


    return isActiveEvent && publicationKind === 'event' && isTheNightly ? (
        <ReportersLive
            authors={reportersLive}
        />
    ) : null
}

const ReportersLiveComponent = ({ authors }: ReportersProps) => {
    const [isOpened, setOpened] = useState(false)

    const authorPressed = useCallback((author: Reporter) => {
        if (author.slug !== undefined) {
            window.open(`/profile/${author.slug}`, '_blank', 'noopener,noreferrer')
        }
    }, [])

    if (!authors || authors.length === 0) {
        return null
    }

    if (authors.length === 1) {
        return (
            <StyledMainView onClick={() => authorPressed(authors[0])}>
                <StyledImageView>
                    <AuthorImage size="small" src={authors[0].thumbnailPhoto} />
                </StyledImageView>
                <ReportingLiveText
                    name1={authors[0].name}
                />
            </StyledMainView>
        )
    }

    if (isOpened) {
        return (
            <StyledOpenedMainView>
                <TitleText>Reporting Live</TitleText>
                    {authors.map((author, index) => (
                        <StyledAuthorsView 
                            key={index}
                            onClick={() => authorPressed(author)}
                        >
                            <AuthorImage size="large" src={author.thumbnailPhoto} />
                            <StyledNameText>{author.name}</StyledNameText>
                        
                        </StyledAuthorsView>
                    ))}
                <StyledLessButton
                    onClick={() => setOpened(false)}
                >
                    <StyledLessButtonText>Less</StyledLessButtonText>
                    <StyledSquareIcon size={24} rotate={180}>
                        <IconChevronDownTN strokeColor='#ffffff'/>
                    </StyledSquareIcon>
                </StyledLessButton>
            </StyledOpenedMainView>
        )
    }

    return (

        <StyledMainView onClick={() => setOpened(true)}>
            <StyledImageView>
                <AuthorImage size="small" src={authors[0].thumbnailPhoto} />
                {authors.length === 2 && (
                    <AuthorImage size="small" isShadowView={true} src={authors[1].thumbnailPhoto} />
                )}
                {authors.length > 2 && (
                    <StyledShadowView>
                        <StyledCountText>+{authors.length - 1}</StyledCountText>
                    </StyledShadowView>
                )}
            </StyledImageView>
            <ReportingLiveText
                name1={authors[0].name}
                name2={
                    authors.length === 2
                        ? authors[1].name
                        : `${authors.length - 1}+ more`
                }
            />
            <StyledSquareIcon size={24}>
                <IconChevronDownTN strokeColor='#ffffff'/>
            </StyledSquareIcon>
        </StyledMainView>

    )
}

export const ReportersLive = React.memo(ReportersLiveComponent)
